/**
 * Unified settings service factory
 * Creates type-safe settings accessors with localStorage persistence
 */

// Create a boolean setting with enable/disable/isEnabled methods
const createBooleanSetting = (key, defaultValue = false) => ({
  isEnabled: () => {
    try {
      const stored = localStorage.getItem(key);
      return stored === null ? defaultValue : stored === 'true';
    } catch {
      return defaultValue;
    }
  },
  enable: () => {
    try { localStorage.setItem(key, 'true'); } catch {}
  },
  disable: () => {
    try { localStorage.setItem(key, 'false'); } catch {}
  },
  toggle: function() {
    const newValue = !this.isEnabled();
    newValue ? this.enable() : this.disable();
    return newValue;
  }
});

// Create a string setting with get/set methods
const createStringSetting = (key, defaultValue = '') => ({
  get: () => {
    try {
      return localStorage.getItem(key) || defaultValue;
    } catch {
      return defaultValue;
    }
  },
  set: (value) => {
    try { localStorage.setItem(key, value); } catch {}
  }
});

// Create a number setting with get/set methods and min/max bounds
const createNumberSetting = (key, defaultValue, min, max) => ({
  get: () => {
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) return defaultValue;
      const value = parseInt(stored, 10);
      return isNaN(value) ? defaultValue : Math.max(min, Math.min(max, value));
    } catch {
      return defaultValue;
    }
  },
  set: (value) => {
    try {
      const bounded = Math.max(min, Math.min(max, value));
      localStorage.setItem(key, bounded.toString());
    } catch {}
  },
  min,
  max,
  default: defaultValue
});

// Cooldown helper for rate-limiting actions
const createCooldown = (cooldownMs) => {
  let lastTime = 0;
  return {
    canTrigger: () => Date.now() - lastTime >= cooldownMs,
    trigger: () => { lastTime = Date.now(); }
  };
};

// Keyword variations for triggers (German + English)
// Multi-word phrases should NOT include single-word shortcuts to avoid premature triggering
// For Vosk: final results may contain single words if user pauses, so keep phrases intact
const START_KEYWORDS = {
  'aufnahme start': ['aufnahme start', 'aufnahme starten', 'start aufnahme', 'recording start', 'start recording'],
  start: ['start', 'starten', 'los', 'beginnen', 'begin'],
};

const STOP_KEYWORDS = {
  'aufnahme stopp': ['aufnahme stopp', 'aufnahme stop', 'stopp aufnahme', 'stop aufnahme', 'aufnahme ende', 'recording stop', 'stop recording'],
  stop: ['stop', 'stopp', 'ende', 'fertig', 'aufhören', 'end', 'finish'],
};

// Environment presets map to noise, pause levels, and beep volume
export const ENVIRONMENT_PRESETS = {
  quietOffice: { noiseLevel: 0, pauseDetection: 0, beepVolume: 30 },
  workshop: { noiseLevel: 2, pauseDetection: 2, beepVolume: 60 },
  outdoor: { noiseLevel: 3, pauseDetection: 3, beepVolume: 80 },
  veryLoud: { noiseLevel: 4, pauseDetection: 4, beepVolume: 100 }
};

// Convert noise level (0-4) to dB value for audio processing
export const getNoiseLevelDb = (level) => {
  const mapping = [-40, -35, -30, -25, -20];
  return mapping[level] || -30;
};

// Convert pause detection level (0-4) to dB value
export const getPauseThresholdDb = (level) => {
  const mapping = [-45, -40, -35, -30, -25];
  return mapping[level] || -35;
};

// Export unified settings
export const settings = {
  shake: {
    ...createBooleanSetting('shake_detection_enabled', false),
    cooldown: createCooldown(2000)
  },
  bluetooth: {
    ...createBooleanSetting('bluetooth_button_enabled', false),
    cooldown: createCooldown(2000)
  },
  mediaButton: {
    ...createBooleanSetting('media_button_enabled', false),
    cooldown: createCooldown(2000)
  },
  voiceTrigger: {
    ...createBooleanSetting('voice_trigger_enabled', false),
    word: createStringSetting('voice_trigger_word', 'Aufnahme Start'),
    getKeywords: function() {
      const word = this.word.get().toLowerCase();
      return START_KEYWORDS[word] || [word];
    }
  },
  stopTrigger: {
    ...createBooleanSetting('stop_trigger_enabled', false),
    word: createStringSetting('stop_trigger_word', 'Aufnahme Stopp'),
    getKeywords: function() {
      const word = this.word.get().toLowerCase();
      return STOP_KEYWORDS[word] || [word];
    }
  },
  triggerLanguage: createStringSetting('trigger_language', 'de-DE'),
  chunkDuration: createNumberSetting('chunk_duration_seconds', 60, 30, 600),
  audioUpload: createBooleanSetting('audio_upload_enabled', true),
  uploadRawChunks: createBooleanSetting('upload_raw_chunks_enabled', false),
  playbackRaw: createBooleanSetting('playback_raw_enabled', false),
  noiseLevel: createNumberSetting('noise_level', 2, 0, 4), // 0=quiet, 4=very loud
  pauseDetection: createNumberSetting('pause_detection', 2, 0, 4), // 0=sensitive, 4=relaxed
  extendedErrorReports: createBooleanSetting('extended_error_reports', false),
  dangerZoneEnabled: createBooleanSetting('danger_zone_enabled', false), // Admin control for danger zone visibility
  selectedInputDevice: createStringSetting('selected_input_device', ''), // Persist selected microphone across sessions
  vibrateOnRecording: createBooleanSetting('vibrate_on_recording', true), // Vibrate phone on recording start/error (Android only, iOS doesn't support navigator.vibrate)
  beepVolume: createNumberSetting('beep_volume', 80, 0, 100) // Beep volume for recording start/stop (0=off, 100=max)
};

// Dispatch a setting change event
export const dispatchSettingChange = (name, detail) => {
  window.dispatchEvent(new CustomEvent(`${name}SettingChanged`, { detail }));
};

// Hook helper: listen for setting changes
export const onSettingChange = (name, callback) => {
  const handler = (e) => callback(e.detail);
  window.addEventListener(`${name}SettingChanged`, handler);
  return () => window.removeEventListener(`${name}SettingChanged`, handler);
};
